// Enums
import { DesigningTools } from '@/ship/Enums/DesigningTools';
import TanglRemarkMessageTypeEnum from '@/utils/TanglService/Enums/TanglRemarkMessageTypeEnum';
import TanglTokenMessageEnum from '@/utils/TanglService/Enums/TanglTokenMessageEnum';

// Models
import { DesignPoint } from '@/ship/Models/Design';

// Interfaces
import { ITanglRemark } from '@/utils/TanglService/Interfaces/ITanglRemark';

// Messages
import { ITanglMessage } from '@/utils/TanglService/Messages/ITanglMessage';
import { IRemarkMessage } from '@/utils/TanglService/Messages/IRemarkMessage';
import { IViewPointMessage } from '@/utils/TanglService/Messages/IViewPointMessage';
import { IToolsMessage } from '@/utils/TanglService/Messages/IToolsMessage';
import { ITokenMessage } from '@/utils/TanglService/Messages/ITokenMessage';

/**
 * x, y, z design point (in 3D)
 */
export interface DesignModelPoint extends DesignPoint {
    z: number;
}

/**
 * Handler for any message from Tangl Viewer
 */
export type ITanglMessageHandler<T extends ITanglMessage> = (event: MessageEvent<T>) => void;

export class TanglService {
    /**
     * Sets remarks to Tangl 3D scene
     * @param iframe iframe ref with Tangl
     * @param remarks remarks that needs to be added on scene
     */
    public static setRemarksToScene(iframe: HTMLIFrameElement, remarks: ITanglRemark[]): void {
        if (iframe.contentWindow) {
            const message: IRemarkMessage = {
                type: TanglRemarkMessageTypeEnum.AddOnScene,
                remarks,
            };
            iframe.contentWindow.postMessage(message, '*');
        }
    }

    /**
     * Moves camera to position.
     * @param iframe iframe ref with Tangl.
     * @param remark remark with camera position data.
     */
    public static applyViewPoint(iframe: HTMLIFrameElement, remark: ITanglRemark): void {
        if (iframe.contentWindow) {
            const message: IViewPointMessage = {
                type: TanglRemarkMessageTypeEnum.ZoomCamera,
                isExternalViewPoint: remark.isExternalViewPoint,
                cameraPosition: remark.cameraPosition!,
                cameraTarget: remark.cameraTarget,
                cameraRotation: remark.cameraRotation!,
                projection: 'perspective',
            };

            iframe.contentWindow.postMessage(message, '*');
        }
    }

    /**
     * Sends a message in Tangl Viewer to switch Tangl mode.
     * @param iframe iframe ref with Tangl.
     * @param cursor current designing editor cursor.
     */
    public static cursorChanged(iframe: HTMLIFrameElement, cursor: DesigningTools) {
        if (iframe.contentWindow) {
            const message: IToolsMessage = {
                type: TanglRemarkMessageTypeEnum.ToolChanged,
                currentTool: cursor,
            };

            iframe.contentWindow.postMessage(message, '*');
        }
    }

    /**
     * Send a message to Tangl Viewer with authentication token.
     * @param iframe iframe ref with Tangl.
     * @param authToken authentication token to be sent.
     */
    public static sendToken(iframe: HTMLIFrameElement, authToken: string) {
        if (iframe.contentWindow) {
            const message: ITokenMessage = {
                type: TanglTokenMessageEnum.Token,
                token: authToken,
            };

            iframe.contentWindow.postMessage(message, '*');
        }
    }
}
